/**
 * TransactionTable - Phase A / Bank Feed Redesign (v2)
 * ====================================================
 *
 * Owner-friendly data-table view of bank-feed transactions used for both
 * "Review Needed" and "Completed" sub-tabs.
 *
 * Improvements vs v1:
 *   • Counterparty and Description columns trimmed; Description is now
 *     inline-editable. Action column has more breathing room with labeled
 *     buttons (icon + short text) so non-accountants understand what each
 *     button does.
 *   • GL Category column shows the meaningful (non-bank) side based on
 *     direction. See `pickCategoryDisplay`.
 *   • VAT / WHT columns show the actual amount + rate when applied (or
 *     suggested), not just a binary toggle.
 *   • Status pulse on rows that need attention.
 */
import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Checkbox } from '../ui/checkbox';
import { humanizeReviewReason } from './reviewReason';
import { ConfirmationProgressChip } from './ConfirmationProgressChip';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  ThumbsUp,
  Edit3,
  Scissors,
  Undo2,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Loader2,
  HelpCircle,
  MoreHorizontal,
  ChevronDown,
  Brain,
  FileText,
  BookOpen,
  Landmark,
  Link2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { GlAccountInlineSelect } from './GlAccountInlineSelect';
import { TransferPairChip, TransferFindOtherSideChip, ExtractionWarningBadge, FeeSuggestionChip, FeeSplitBadge, StripeHoldChip } from './StatementIntegrityChips';
import { TaxRateSelect } from './TaxRateSelect';
import { CounterpartyTaxFix } from './CounterpartyTaxFix';
import { EditableDescriptionCell } from './EditableDescriptionCell';
import { InlineTextEditor } from './InlineTextEditor';
import { ProjectSelect } from '../projects/ProjectSelect';
import { LinkedCell } from './LinkedCell';
import { BftAttachmentsButton } from './BftAttachmentsButton';
import { ChannelChip } from './ChannelChip';
import { pickCategoryDisplay } from './bankFeedDisplay';
import { formatCurrency } from '../../utils/currency';
import { rowCurrencyOf, RowFxEquivalent } from './RowFxEquivalent';

const PAGE_SIZE_DEFAULT = 50;

const confidenceTone = (c) => {
  if (c == null) return { tone: 'bg-slate-100 text-slate-600', label: '-' };
  if (c >= 0.80) return { tone: 'bg-emerald-100 text-emerald-700', label: 'High' };
  if (c >= 0.65) return { tone: 'bg-amber-100 text-amber-800', label: 'Med' };
  return { tone: 'bg-red-100 text-red-700', label: 'Low' };
};

const HeaderTip = ({ children, body }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-1 cursor-help">
          {children}
          <HelpCircle className="w-3 h-3 opacity-60" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[260px] text-xs leading-snug">{body}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

/** Action button with icon + label, accessible to non-accountants.
 *  The label was previously shown only on `xl` viewports
 *  (`hidden xl:inline`). Per user feedback that hovering the icon-only
 *  buttons gave no hint about what they did, we now ALWAYS expose the
 *  label as a tooltip on hover/focus. The text-on-button still
 *  collapses on smaller viewports, but the tooltip is universal. */
const ActionButton = ({ label, tone = 'default', onClick, disabled, icon: Icon, testId, busy, alwaysLabel = false, compact = false }) => {
  const toneClass = {
    default: 'text-slate-600 hover:bg-slate-100 hover:text-slate-800',
    approve: 'text-emerald-700 hover:bg-emerald-50 border-emerald-200',
    edit:    'text-blue-700 hover:bg-blue-50 border-blue-200',
    split:   'text-purple-700 hover:bg-purple-50 border-purple-200',
    undo:    'text-orange-700 hover:bg-orange-50 border-orange-200',
    link:    'text-sky-800 bg-sky-50/70 hover:bg-sky-100 border border-sky-300',
  }[tone] || '';
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClick}
            disabled={disabled}
            className={`h-7 gap-1 font-medium whitespace-nowrap ${compact ? 'px-1.5 text-[10.5px]' : 'px-2 text-[11px]'} ${toneClass}`}
            data-testid={testId}
            aria-label={label}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
            <span className={alwaysLabel ? 'inline' : 'hidden xl:inline'}>{label}</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const TaxCell = ({ kind, tx, taxCtx, ts, onFetchStatus, onSaved }) => {
  // iter971k-E - Table view now uses a native dropdown selector so users
  // can pick from country-tax-pack rates (or "No tax") on every row.
  // Column visibility is decided upstream in `taxCtx` (country-driven);
  // the row itself always renders the dropdown when the column is shown.
  const kindKey = kind === 'vat' ? 'vat' : 'wht';

  // Trigger tax-status fetch on mount if the parent hasn't already -
  // preserves the lazy-load pattern from TaxToggleButtons/legacy TaxCell.
  // iter971k-H FIX - the previous check `!ts` was truthy for empty `{}`
  // (the default when parent hasn't fetched yet), so the useEffect never
  // fired and users NEVER saw AI VAT/WHT suggestions on the Review Needed
  // tab. Fixed to check for absence of the actual suggestion payload.
  const fetchAttemptedRef = React.useRef(false);
  React.useEffect(() => {
    const hasStatus = ts && (
      ts.suggested_vat !== undefined ||
      ts.suggested_wht !== undefined ||
      ts.can_apply_vat !== undefined ||
      ts.can_apply_wht !== undefined
    );
    if (!hasStatus && !fetchAttemptedRef.current && onFetchStatus && tx?.id) {
      fetchAttemptedRef.current = true;
      onFetchStatus(tx.id);
    }
  }, [tx?.id, ts, onFetchStatus]);

  return (
    <div className="flex items-center justify-center gap-1" data-testid={`tax-cell-${kindKey}-${tx.id}`}>
      <TaxRateSelect
        kind={kindKey}
        transaction={tx}
        taxCtx={taxCtx}
        taxStatus={ts}
        onFetchStatus={onFetchStatus}
        onSaved={onSaved}
      />
      {kindKey === 'vat' && ts && (
        <CounterpartyTaxFix item={tx} taxStatus={ts} compact
          onChanged={(txId, fresh) => { onFetchStatus?.(txId, true); onSaved?.(fresh); }} />
      )}
    </div>
  );
};

export const TransactionTable = ({
  mode = 'pending',             // 'pending' | 'completed'
  transactions = [],
  currency = 'NGN',
  baseCurrency = null,          // business base currency (FX equivalent under foreign rows)
  glAccounts = [],
  bankAccounts = [],
  taxCtx,
  // selection
  selectedIds,                  // Set<string>
  onToggleSelect,
  onToggleSelectAll,
  // row-level handlers
  onView,
  onApprove,
  onReclassify,
  onSplit,
  onTaxPayment,
  onMakeRule,                   // iter 740 P0-C: (tx) => Promise
  onViewDocument,               // iter 807: (tx) => void, opens the linked invoice/bill (Completed mode)
  onViewDetails,                // (tx) => void, opens the Transaction Details dialog (posted GL lines)
  onUndoClassification,
  onChangeGl,                   // (tx, glAccount) => Promise
  onChangeProject,              // (tx, projectId|null) => Promise: inline project allocation
  pendingGlEdits = new Map(),   // Phase #5. Map<txId, { glCode, glName }> for staged edits
  onSaveRow,                    // Phase 1.3: (tx) => Promise, single-row save for Completed rows
  onChangeDescription,          // (tx, newDescription) => Promise
  onRequestAiSuggestion,        // (tx, newDescription) => Promise<suggestion>
  onChangeCounterparty,         // (tx, newName) => Promise. Jul 17 contact fix
  onApplyAiSuggestion,          // (tx, suggestion) => Promise
  onUnlinkDocument,             // (tx) => Promise. Phase H
  onOpenLinkDialog,             // (tx) => void: opens LinkDocumentDialog
  onToggleVat,                  // (tx) => Promise
  onToggleWht,                  // (tx) => Promise
  taxStatus = {},               // { [txId]: { has_vat, has_wht } }
  onFetchTaxStatus,             // iter971k-C: (txId) => void; lazy-fetches tax-status for a row
  isApproving,
  pageSize = PAGE_SIZE_DEFAULT,
  // iter 971e - refetch after "mark as bank transfer" confirmation
  onAfterTransfer,
  // iter1404 Phase 4b · open the In Books drill (per-tx journal + linked docs).
  onOpenInBooks,
}) => {
  const [page, setPage] = useState(1);
  // Mobile (<768px): the table re-flows into stacked cards; each cell gets its column label so nothing is lost.
  const tableRef = useRef(null);
  useEffect(() => {
    const root = tableRef.current; if (!root) return;
    const heads = Array.from(root.querySelectorAll('thead th')).map((th) => (th.textContent || '').trim());
    root.querySelectorAll('tbody tr').forEach((tr) => {
      Array.from(tr.children).forEach((td, idx) => {
        const label = heads[idx] || '';
        td.setAttribute('data-label', label);
        if (/^(Description|Actions|Category|Link existing transaction|Linked|Project)$/i.test(label)) td.setAttribute('data-span', '2');
        if (/^Amount$/i.test(label)) td.setAttribute('data-align', 'right');
        if (label === '' && idx === 0) td.setAttribute('data-label', 'Select');
      });
    });
  });

  const [savingGlId, setSavingGlId] = useState(null);

  const total = transactions.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const slice = useMemo(
    () => transactions.slice((safePage - 1) * pageSize, safePage * pageSize),
    [transactions, safePage, pageSize],
  );
  const allOnPageSelected = slice.length > 0 && slice.every(t => selectedIds?.has(t.id));

  const handleGlChange = useCallback(async (tx, account) => {
    setSavingGlId(tx.id);
    try { await onChangeGl?.(tx, account); } finally { setSavingGlId(null); }
  }, [onChangeGl]);

  const bankNameById = useMemo(() => {
    // Show "{bank_name} · …{last4}" so users with multiple accounts can tell
    // them apart at a glance. Falls back to bank name + account name if no
    // account number is present.
    const map = new Map();
    bankAccounts.forEach((b) => {
      const acctNum = b.account_number || '';
      const last4 = acctNum.length >= 4 ? acctNum.slice(-4) : acctNum;
      const bank = b.bank_name || b.name || 'Bank';
      map.set(b.id, last4 ? `${bank} · …${last4}` : (b.account_name ? `${bank} · ${b.account_name}` : bank));
    });
    return map;
  }, [bankAccounts]);

  const showVat = !!taxCtx?.show_vat;
  const showWht = !!taxCtx?.show_wht;
  const showSalesTax = !!taxCtx?.show_sales_tax;
  const vatLabel = taxCtx?.vat_label || 'VAT';

  if (transactions.length === 0) return null;

  return (
    <div className="w-full" data-testid={`tx-table-${mode}`} ref={tableRef}>
      <div className="akf-rtable overflow-x-auto border border-slate-300 rounded-xl bg-card shadow-sm [&_td]:border-r [&_td]:border-slate-200/80 [&_td:last-child]:border-r-0 [&_th]:border-r [&_th]:border-slate-200 [&_th:last-child]:border-r-0 [&_tbody_tr]:border-b [&_tbody_tr]:border-slate-200 [&_tbody_tr:nth-child(even)]:bg-slate-50/50" data-testid="tx-table-grid">
        <Table className="text-sm">
          <TableHeader>
            <TableRow className="bg-slate-100 hover:bg-slate-100 border-b-2 border-slate-300 [&_th]:py-2.5 [&_th]:text-[11px] [&_th]:font-semibold [&_th]:uppercase [&_th]:tracking-wider [&_th]:text-slate-600 sticky top-0 z-10">
              <TableHead className="w-10 p-0 sticky left-0 bg-slate-100 dark:bg-slate-800 z-20">
                <div className="flex items-center justify-center w-10 h-full min-h-[40px]">
                  <Checkbox
                    checked={allOnPageSelected}
                    onCheckedChange={() => onToggleSelectAll?.(slice)}
                    data-testid="tx-table-select-page"
                    aria-label="Select page"
                  />
                </div>
              </TableHead>
              {mode === 'pending' && (
                <>
                  <TableHead className="w-[96px] text-center sticky left-10 bg-slate-100 dark:bg-slate-800 z-20">Approve</TableHead>
                  <TableHead className="w-[168px] text-center sticky left-[136px] bg-slate-100 dark:bg-slate-800 z-20" data-testid="tx-table-link-existing-head">
                    <HeaderTip body="Already recorded this in your books (invoice, bill, loan, asset, prepayment…)? Link the bank line to that existing transaction instead of creating a new one.">
                      Link existing transaction
                    </HeaderTip>
                  </TableHead>
                </>
              )}
              <TableHead className="w-[72px]">Date</TableHead>
              <TableHead className="w-[88px]">Bank</TableHead>
              <TableHead className="w-[120px]">Counterparty</TableHead>
              <TableHead className="w-[150px]">Description</TableHead>
              <TableHead className="text-right w-[100px]">Amount</TableHead>
              <TableHead className="w-[170px]">
                <HeaderTip body="The non-bank side this transaction posts to in your books: i.e. where the money came from (for inflows) or went to (for outflows). Click to change.">
                  Category
                </HeaderTip>
              </TableHead>
              <TableHead className="w-[150px]">
                <HeaderTip body="Optional project allocation: tags this transaction (and its journal when posted) so it shows in project P&L.">
                  Project
                </HeaderTip>
              </TableHead>
              {showVat && (
                <TableHead className="w-20 text-center">
                  <HeaderTip body={showSalesTax
                    ? `State sales tax decision for this row${taxCtx?.sales_tax_state ? ` (home state ${taxCtx.sales_tax_state}, default ${taxCtx.sales_tax_rate}%)` : ''}. Money in: the tax collected is split out to 2280 Sales Tax Payable. Money out: sales tax you paid stays in the cost (not recoverable).`
                    : `${vatLabel} amount applied. Inflows post Output ${vatLabel} (payable to tax authority); outflows post Input ${vatLabel} (recoverable).`}>
                    {vatLabel}
                  </HeaderTip>
                </TableHead>
              )}
              {showWht && (
                <TableHead className="w-20 text-center">
                  <HeaderTip body="Withholding tax. Inflows post WHT Receivable (asset: tax credit your customer withheld); outflows post WHT Payable (liability. Tax you owe the tax authority).">
                    WHT
                  </HeaderTip>
                </TableHead>
              )}
              <TableHead className="w-16 text-center">
                <HeaderTip body="How confident AkountFly is in this category. High ≥80%, Med 65–79%, Low <65%.">
                  AI
                </HeaderTip>
              </TableHead>
              <TableHead className="w-20">Linked</TableHead>
              <TableHead className="text-left w-[180px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {slice.map(tx => {
              // Trust `direction` only. `amount` is stored as an absolute
              // magnitude server-side, so `amount > 0` always evaluates true
              // for outflows and incorrectly painted every row green with a
              // down-right (inflow) arrow.
              const isInflow = tx.direction === 'inflow';
              const amount = Math.abs(tx.amount || 0);
              const ts = taxStatus[tx.id] || {};
              const conf = tx.confidence ?? tx.ai_confidence;
              const tone = confidenceTone(conf);
              const cat = pickCategoryDisplay(tx);
              const isSelected = !!selectedIds?.has(tx.id);
              const needsAttention = mode === 'pending' && (conf == null || conf < 0.65);
              // Phase #5 - show staged category override if a Save Changes
              // edit is queued for this row, with a subtle yellow accent on
              // the left of the row so the user can see what's pending.
              const pendingEdit = pendingGlEdits?.get(tx.id);
              const displayCat = pendingEdit
                ? { code: pendingEdit.glCode, name: pendingEdit.glName, side: cat.side, isPending: true }
                : cat;
              return (
                <TableRow
                  key={tx.id}
                  data-testid={`tx-row-${tx.id}`}
                  className={`align-top group ${isSelected ? '!bg-blue-50 ring-1 ring-inset ring-blue-200' : ''} ${needsAttention ? 'ring-1 ring-amber-200/60' : ''} ${pendingEdit ? 'border-l-2 border-l-amber-400 !bg-amber-50/40' : ''} hover:!bg-sky-50/70 transition-colors [&_td]:py-2.5`}
                >
                  <TableCell className="w-10 p-0 sticky left-0 bg-white dark:bg-slate-950 z-20">
                    <div className="flex items-center justify-center w-10 h-full min-h-[44px]">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => onToggleSelect?.(tx.id)}
                        data-testid={`tx-row-select-${tx.id}`}
                      />
                    </div>
                  </TableCell>
                  {mode === 'pending' && (
                    <>
                      <TableCell className="text-center sticky left-10 bg-white dark:bg-slate-950 z-20">
                        <ActionButton
                          label="Approve"
                          tone="approve"
                          alwaysLabel
                          icon={ThumbsUp}
                          onClick={() => onApprove?.(tx)}
                          disabled={isApproving === tx.id}
                          busy={isApproving === tx.id}
                          testId={`tx-approve-${tx.id}`}
                        />
                      </TableCell>
                      <TableCell className="text-center sticky left-[136px] bg-white dark:bg-slate-950 z-20">
                        {onOpenLinkDialog && (
                          <ActionButton
                            label="Link existing transaction"
                            tone="link"
                            alwaysLabel
                            compact
                            icon={Link2}
                            onClick={() => onOpenLinkDialog?.(tx)}
                            testId={`tx-link-doc-inline-${tx.id}`}
                          />
                        )}
                      </TableCell>
                    </>
                  )}
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <span>{tx.date ? new Date(tx.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' }) : '-'}</span>
                      <ChannelChip item={tx} size="xs" />
                    </div>
                  </TableCell>
                  <TableCell className="text-xs truncate max-w-[88px]" title={bankNameById.get(tx.bank_account_id) || tx.bank_name || ''}>
                    {bankNameById.get(tx.bank_account_id) || tx.bank_name || '-'}
                  </TableCell>
                  <TableCell className="font-medium text-foreground max-w-[120px]">
                    <InlineTextEditor
                      value={tx.counterparty || tx.ai_counterparty || ''}
                      placeholder="Add contact…"
                      label="Contact name"
                      truncateAt={18}
                      textClass="text-xs font-medium text-foreground"
                      onSave={(next) => onChangeCounterparty?.(tx, next)}
                      testIdPrefix={`tx-contact-${tx.id}`}
                    />
                  </TableCell>
                  <TableCell className="max-w-[150px]">
                    <EditableDescriptionCell
                      value={tx.description}
                      reference={tx.reference}
                      onSave={(next) => onChangeDescription?.(tx, next)}
                      onRequestAiSuggestion={onRequestAiSuggestion ? (next) => onRequestAiSuggestion(tx, next) : undefined}
                      onApplyAiSuggestion={onApplyAiSuggestion ? (sugg) => onApplyAiSuggestion(tx, sugg) : undefined}
                      disabled={!onChangeDescription}
                      testId={`tx-desc-${tx.id}`}
                    />
                    {(tx.fee_suggestion || tx.extraction_warning || (tx.fee_split && !tx.fee_split.reverted) || tx.review_reason_code === 'stripe_payout_awaiting_details') && (
                      <div className="mt-0.5 flex flex-wrap gap-1">
                        <StripeHoldChip tx={tx} />
                        <FeeSuggestionChip tx={tx} />
                        <ExtractionWarningBadge tx={tx} />
                        <FeeSplitBadge tx={tx} onDone={onAfterTransfer} />
                      </div>
                    )}
                    {tx.status === 'review_needed' && (tx.processing_reasoning || tx.ai_suggestion || tx.ai_reasoning) && (
                      <div
                        className="mt-0.5 flex items-start gap-1 text-[11px] leading-snug text-amber-700"
                        data-testid={`why-review-${tx.id}`}
                        title={humanizeReviewReason(tx.processing_reasoning || tx.ai_suggestion || tx.ai_reasoning)}
                      >
                        <HelpCircle className="w-3 h-3 mt-[1px] shrink-0" />
                        <span className="line-clamp-2">{humanizeReviewReason(tx.processing_reasoning || tx.ai_suggestion || tx.ai_reasoning)}</span>
                      </div>
                    )}
                    {tx.status === 'review_needed' && tx.confirmation_progress && (
                      <div className="mt-1"><ConfirmationProgressChip item={tx} compact /></div>
                    )}
                  </TableCell>
                  <TableCell className={`text-right font-semibold tabular-nums whitespace-nowrap ${isInflow ? 'text-emerald-700' : 'text-rose-700'}`}>
                    <span className="inline-flex items-center gap-1">
                      {isInflow ? <ArrowDownRight className="w-3.5 h-3.5" /> : <ArrowUpRight className="w-3.5 h-3.5" />}
                      {isInflow ? '+' : '-'}{formatCurrency(amount, rowCurrencyOf(tx, bankAccounts, currency))}
                    </span>
                    <RowFxEquivalent tx={tx} rowCurrency={rowCurrencyOf(tx, bankAccounts, currency)} baseCurrency={baseCurrency} className="block font-normal" />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      <GlAccountInlineSelect
                        value={displayCat.code}
                        valueName={displayCat.name}
                        accounts={glAccounts}
                        onChange={(account) => handleGlChange(tx, account)}
                        saving={savingGlId === tx.id}
                        direction={tx.direction}
                        edited={!!pendingEdit}
                        testId={`gl-edit-${tx.id}`}
                      />
                      {/* iter 998 / Oct 2026 - transfers are only recorded through the paired
                          review dialog: best match pre-selected, other candidates listed. */}
                      {tx.transfer_pair && !tx.transfer_pair.confirmed && tx.status !== 'completed' ? (
                        <div className="mt-1 min-w-[200px]">
                          <TransferPairChip tx={tx} onDone={onAfterTransfer} compact />
                        </div>
                      ) : (
                        <div className="mt-1 empty:hidden">
                          <TransferFindOtherSideChip tx={tx} onDone={onAfterTransfer} />
                        </div>
                      )}
                      {displayCat.isPending && (
                        <div className="flex items-center gap-1.5">
                          {mode === 'pending' ? (
                            <span
                              className="text-[9px] font-medium text-amber-700 uppercase tracking-wide"
                              data-testid={`gl-pending-${tx.id}`}
                              title="Your GL change will be applied when you click Approve"
                            >
                              • Will apply on Approve
                            </span>
                          ) : (
                            <>
                              <span
                                className="text-[9px] font-medium text-amber-700 uppercase tracking-wide"
                                data-testid={`gl-pending-${tx.id}`}
                              >
                                • Unsaved
                              </span>
                              {onSaveRow && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-5 px-1.5 text-[10px] font-medium border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                  onClick={() => onSaveRow(tx)}
                                  data-testid={`gl-save-row-${tx.id}`}
                                >
                                  Save
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <ProjectSelect
                      compact
                      value={tx.project_id || null}
                      onChange={(pid) => onChangeProject?.(tx, pid)}
                      createdFrom="bank_feed"
                      autoMatched={!!tx.project_auto_matched}
                      className="w-full max-w-[150px]"
                    />
                  </TableCell>
                  {showVat && (
                    <TableCell>
                      <TaxCell
                        kind="vat"
                        tx={tx}
                        taxCtx={taxCtx}
                        ts={ts}
                        onFetchStatus={onFetchTaxStatus}
                        onSaved={onAfterTransfer}
                      />
                    </TableCell>
                  )}
                  {showWht && (
                    <TableCell>
                      <TaxCell
                        kind="wht"
                        tx={tx}
                        taxCtx={taxCtx}
                        ts={ts}
                        onFetchStatus={onFetchTaxStatus}
                        onSaved={onAfterTransfer}
                      />
                    </TableCell>
                  )}
                  <TableCell className="text-center">
                    {conf != null && (
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${tone.tone}`}>
                        <Sparkles className="w-2.5 h-2.5" />
                        {Math.round((conf || 0) * 100)}%
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <LinkedCell tx={tx} onUnlink={onUnlinkDocument} onOpenLink={onOpenLinkDialog} />
                      <BftAttachmentsButton bftId={tx.id} compact />
                    </div>
                  </TableCell>
                  <TableCell className="text-left">
                    <div className="inline-flex items-center gap-0.5 justify-start">
                      <ActionButton
                        label="View"
                        tone="default"
                        icon={Eye}
                        onClick={() => onView?.(tx)}
                        testId={`tx-view-${tx.id}`}
                      />
                      {mode === 'pending' && (
                        <>
                          {onReclassify && (
                            <ActionButton
                              label="Re-classify"
                              tone="default"
                              icon={Edit3}
                              onClick={() => onReclassify?.(tx)}
                              testId={`tx-reclassify-inline-${tx.id}`}
                            />
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-[11px] gap-1 border-slate-300 text-slate-700 hover:bg-slate-100 font-medium"
                                data-testid={`tx-more-${tx.id}`}
                                aria-label="More actions"
                                title="More actions: Re-classify, Split, Make a rule, View details"
                              >
                                <MoreHorizontal className="w-4 h-4" />
                                <span>More</span>
                                <ChevronDown className="w-3 h-3 opacity-70" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              <DropdownMenuItem
                                onSelect={() => onReclassify?.(tx)}
                                data-testid={`tx-reclassify-${tx.id}`}
                                className="gap-2 text-xs"
                              >
                                <Edit3 className="w-3.5 h-3.5 text-blue-600" />
                                Re-classify
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() => onSplit?.(tx)}
                                data-testid={`tx-split-${tx.id}`}
                                className="gap-2 text-xs"
                              >
                                <Scissors className="w-3.5 h-3.5 text-purple-600" />
                                Split transaction
                              </DropdownMenuItem>
                              {onTaxPayment && !isInflow && (
                                <DropdownMenuItem
                                  onSelect={() => onTaxPayment?.(tx)}
                                  data-testid={`tx-tax-payment-${tx.id}`}
                                  className="gap-2 text-xs"
                                >
                                  <Landmark className="w-3.5 h-3.5 text-[#283F8B]" />
                                  Record income tax payment
                                </DropdownMenuItem>
                              )}
                              {onMakeRule && (
                                <DropdownMenuItem
                                  onSelect={() => onMakeRule?.(tx)}
                                  data-testid={`tx-make-rule-${tx.id}`}
                                  className="gap-2 text-xs"
                                >
                                  <Brain className="w-3.5 h-3.5 text-emerald-600" />
                                  Make this a rule
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onSelect={() => onView?.(tx)}
                                data-testid={`tx-view-${tx.id}`}
                                className="gap-2 text-xs"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                View details
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </>
                      )}
                      {mode === 'completed' && (
                        <>
                          {/* Iter 807 - Completed Table view parity with Card:
                              View-Document button, Split, and Undo. */}
                          {onUndoClassification && (
                            <ActionButton
                              label="Undo"
                              tone="undo"
                              icon={Undo2}
                              onClick={() => onUndoClassification?.(tx)}
                              testId={`undo-classification-${tx.id}`}
                            />
                          )}
                          {onSplit && (
                            <ActionButton
                              label="Split"
                              tone="amber"
                              icon={Scissors}
                              onClick={() => onSplit?.(tx)}
                              testId={`tx-split-completed-${tx.id}`}
                            />
                          )}
                          {onViewDocument && tx.document_id && tx.document_type && (
                            <ActionButton
                              label="View doc"
                              icon={FileText}
                              onClick={() => onViewDocument?.(tx)}
                              testId={`tx-view-doc-${tx.id}`}
                            />
                          )}
                          {onViewDetails && (
                            <ActionButton
                              label="Details"
                              icon={Eye}
                              onClick={() => onViewDetails?.(tx)}
                              testId={`tx-view-details-${tx.id}`}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination - centered so the AI co-pilot bubble never covers Next */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:grid sm:grid-cols-[1fr_auto_1fr] items-center gap-2 mt-3 text-xs text-muted-foreground" data-testid="tx-table-pager">
          <span className="sm:justify-self-start">
            Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, total)} of {total}
          </span>
          <div className="flex items-center gap-2 sm:justify-self-center">
            <Button size="sm" variant="outline" className="h-7 px-2" disabled={safePage === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <span className="tabular-nums">Page {safePage} / {totalPages}</span>
            <Button size="sm" variant="outline" className="h-7 px-2" disabled={safePage === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="hidden sm:block" />
        </div>
      )}
    </div>
  );
};

export default TransactionTable;
